import { memo, useCallback, useRef } from 'react';

import filesize from 'filesize';
import { CloudUpload, MousePointer2 } from 'lucide-react';
import DropzoneComponent from 'react-dropzone';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { uploadConfig } from '@/client/media';
import { notify } from '@/components/toast';

type Props = {
  onDropZoneEnter: () => void;
  onDropZoneLeave: () => void;
  onDrop: () => void;
  dropZoneActive: boolean;
  title?: string;
  buttonLabel?: string;
  error?: any;
  maxFileSize?: number;
  className?: string;
};

export const DropZone = memo(
  ({
    className,
    onDropZoneEnter,
    onDropZoneLeave,
    onDrop,
    dropZoneActive,
    title = 'Drag a file here',
    buttonLabel = 'select a file from your device',
    error = null,
    maxFileSize = uploadConfig.MAX_FILE_SIZE
  }: Props) => {
    const dropZoneRef = useRef<DropzoneComponent>(null);

    const onSelectFileClick = useCallback(() => {
      dropZoneRef.current?.open();
    }, [dropZoneRef]);

    const handleOnDropRejected = file => {
      const { name, size } = file[0];

      notify.error(
        `The size limit is ${filesize(maxFileSize)}, your ${name} is ${filesize(
          size
        )}`
      );
    };

    const { MAX_FILE_SIZE } = uploadConfig;

    return (
      // @ts-expect-error DropzoneComponent is incorrectly typed
      <DropzoneComponent
        className={cn(
          'rounded-lg border border-input bg-card/60 p-1 backdrop-blur-md',
          className
        )}
        data-cy="dropzone"
        data-drag-enter={dropZoneActive}
        disableClick
        maxSize={MAX_FILE_SIZE}
        onDragEnter={onDropZoneEnter}
        onDragLeave={onDropZoneLeave}
        onDrop={onDrop}
        onDropRejected={handleOnDropRejected}
        ref={dropZoneRef}
      >
        <div className="flex size-full flex-col items-center gap-3 rounded-sm border border-input border-dashed bg-card p-4 py-6 text-card-foreground">
          <CloudUpload className="my-4 text-card-foreground/50" size={35} />
          <div className="text-card-foreground/80 text-sm">{title}</div>
          <span className="font-semibold text-[10px] text-muted-foreground">
            OR
          </span>
          <Button
            aria-label="Select file from device"
            data-cy="select-file-from-device"
            onClick={onSelectFileClick}
            type="button"
          >
            <MousePointer2 />
            {buttonLabel}
          </Button>
          {error && (
            <div className="mt-5 flex flex-row flex-nowrap items-center text-destructive text-xs">
              <div className="mr-1 font-semibold">{error.fileName}</div>
              <div>{error.message}</div>
              {error.additionalInfo && (
                <div className="ml-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="cursor-default rounded-full bg-destructive px-1 font-bold text-[9px] text-destructive-foreground">
                          ?
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {error.additionalInfo}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          )}
        </div>
      </DropzoneComponent>
    );
  }
);
